import { IconButton, Autocomplete, TextField } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import i18n from "i18next";
import { FieldPath, useController, UseControllerProps } from "react-hook-form";
import { getFromMailboxOptions } from "src/components/views/tenantMail/fromMailAddressUtils.ts";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";

export interface FormFromMailFieldProps<
	TFieldValues extends object,
	TFieldName extends FieldPath<TFieldValues>,
> extends Pick<UseControllerProps<TFieldValues, TFieldName>, "control" | "name" | "rules"> {
	currentUserEmail: string;
	recentMailboxes: string[];
	onDeleteRecentMailbox: (mailbox: string) => void;
	disabled?: boolean;
	sx?: SxProps<Theme>;
}

export const FormFromMailField = <
	TFieldValues extends object,
	TFieldName extends FieldPath<TFieldValues>,
>({
	control,
	name,
	rules,
	currentUserEmail,
	recentMailboxes,
	onDeleteRecentMailbox,
	disabled: disabledProp = false,
	sx,
}: FormFromMailFieldProps<TFieldValues, TFieldName>) => {
	const { field, fieldState } = useController({
		name,
		control,
		rules,
	});

	const value = typeof field.value === "string" ? field.value : "";
	const options = getFromMailboxOptions(currentUserEmail, recentMailboxes);
	const recentMailboxSet = new Set(recentMailboxes.map((mailbox) => mailbox.trim()));

	return (
		<Autocomplete
			freeSolo
			openOnFocus
			options={options}
			value={value}
			onChange={(_, newValue) => {
				field.onChange(typeof newValue === "string" ? newValue : "");
			}}
			onInputChange={(_, newInputValue, reason) => {
				if (reason !== "reset") {
					field.onChange(newInputValue);
				}
			}}
			sx={sx}
			disabled={field.disabled || disabledProp}
			renderOption={(props, option) => {
				const { key, ...optionProps } = props;
				const isDeletableRecentMailbox = option !== currentUserEmail && recentMailboxSet.has(option);

				return (
					<li key={key} {...optionProps}>
						<HorizontalBox sx={{ alignItems: "center", justifyContent: "space-between", width: "100%" }}>
							<span>{option}</span>
							{isDeletableRecentMailbox && (
								<IconButton
									size={"small"}
									edge={"end"}
									aria-label={i18n.t("delete")}
									onMouseDown={(event) => {
										event.preventDefault();
										event.stopPropagation();
									}}
									onClick={(event) => {
										event.preventDefault();
										event.stopPropagation();
										onDeleteRecentMailbox(option);
									}}
								>
									<FontAwesomeIcon icon={faXmark} />
								</IconButton>
							)}
						</HorizontalBox>
					</li>
				);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label={i18n.t("from_email")}
					placeholder={currentUserEmail}
					onBlur={field.onBlur}
					inputRef={field.ref}
					error={fieldState.error !== undefined}
					helperText={fieldState.error?.message ?? i18n.t("leave_empty_to_send_as_yourself")}
				/>
			)}
		/>
	);
};
